import { useEffect, useState, type FormEvent } from 'react'
import { Clock3, FileText, ImageIcon, Plus, UserRound, Clapperboard } from 'lucide-react'
import Layout from '@/components/Layout'
import AdminDialog from '@/components/AdminDialog'
import { FilterSelect } from '@/components/FilterDropdown'
import ProductionCommissionEditor from './ProductionCommissionEditor'
import ProductionCommissionDetail from './ProductionCommissionDetail'
import ProductionCommissionCover from './ProductionCommissionCover'
import { productionCommissionsApi, type CommissionStats, type CommissionUploads } from '@/api/productionCommissions'
import { errorMessage } from '@/api/client'
import { useAuthStore } from '@/store/auth'
import { useVisibleRefresh } from '@/hooks/useVisibleRefresh'
import {
  commissionRateLabel,
  exampleCommissions,
  recruitmentLabels,
  type ProductionCommission,
  type RecruitmentStatus,
} from './productionCommissionsModel'

const pageSize = 8

function statusStyle(status: RecruitmentStatus) {
  if (status === 'recruiting') return 'bg-green-50 text-green-700'
  if (status === 'paused') return 'bg-amber-50 text-amber-800'
  return 'bg-slate-100 text-slate-600'
}

export default function ProductionCommissions({ prototypeMode = false }: { prototypeMode?: boolean }) {
  const preview = prototypeMode
  const adminRole = useAuthStore(state => state.admin?.role)
  const canManage = preview || adminRole === 'operator' || adminRole === 'super_admin'
  const [commissions, setCommissions] = useState<ProductionCommission[]>(() => preview ? exampleCommissions : [])
  const [stats, setStats] = useState<CommissionStats>({ total: 0, recruiting: 0, reviewing: 0, awaitingContract: 0, producing: 0, pendingApplications: 0 })
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(!preview)
  const [loadError, setLoadError] = useState('')
  const [refreshVersion, setRefreshVersion] = useState(0)
  const [input, setInput] = useState('')
  const [keyword, setKeyword] = useState('')
  const [status, setStatus] = useState('')
  const [page, setPage] = useState(1)
  const [jump, setJump] = useState('1')
  const [editor, setEditor] = useState<{ offer?: ProductionCommission } | null>(null)
  const [detailOffer, setDetailOffer] = useState<ProductionCommission | null>(null)
  const [notice, setNotice] = useState('')
  const [deleteBusyId, setDeleteBusyId] = useState('')
  const [deleteTarget, setDeleteTarget] = useState<ProductionCommission | null>(null)
  const [deleteConfirmTitle, setDeleteConfirmTitle] = useState('')
  useVisibleRefresh(() => setRefreshVersion(current => current + 1), !preview)
  useEffect(() => {
    if (preview) return
    let active = true
    setLoading(true)
    setLoadError('')
    void productionCommissionsApi.list(page, keyword, status).then((data) => {
      if (!active) return
      setCommissions(data.items.map(item => ({ ...item, applications: [] })))
      setStats(data.stats)
      setTotal(data.total)
    }).catch((cause) => { if (active) setLoadError(errorMessage(cause)) })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [preview, page, keyword, status, refreshVersion])
  const filtered = preview ? commissions.filter((item) =>
    (!keyword || item.title.toLowerCase().includes(keyword.toLowerCase())) &&
    (!status || item.status === status),
  ) : commissions
  const recordCount = preview ? filtered.length : total
  const pages = Math.max(1, Math.ceil(recordCount / pageSize))
  const currentPage = Math.min(page, pages)
  const visible = preview ? filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize) : filtered

  async function save(value: ProductionCommission, uploads: CommissionUploads) {
    if (preview) {
      setCommissions((current) => editor?.offer
        ? current.map((item) => item.id === value.id ? value : item)
        : [value, ...current])
    } else {
      await productionCommissionsApi.save(value, uploads, Boolean(editor?.offer))
      setRefreshVersion(current => current + 1)
    }
    setEditor(null)
    setNotice(editor?.offer ? '商单修改已保存' : '商单已保存')
    setPage(1)
  }

  async function remove(item: ProductionCommission, mode: 'archive' | 'permanent') {
    const revision = item.revision ?? 0
    if (!preview && revision < 1) { setLoadError('商单版本缺失，请刷新后重试'); return }
    setDeleteBusyId(item.id)
    setLoadError('')
    try {
      if (!preview) await productionCommissionsApi.delete(item.id, revision, mode)
      setCommissions(current => current.filter(value => value.id !== item.id))
      setNotice(mode === 'archive' ? '商单已隐藏，相关记录仍可用于审计' : '商单已删除')
      setDeleteTarget(null)
      if (!preview) setRefreshVersion(current => current + 1)
    } catch (cause) {
      setLoadError(errorMessage(cause))
    } finally {
      setDeleteBusyId('')
    }
  }

  function search(event: FormEvent) {
    event.preventDefault()
    setKeyword(input.trim())
    setPage(1)
  }

  return (
    <Layout prototypeMode={prototypeMode}>
      <div className="flex min-h-0 flex-1 flex-col">
        <p className="mb-3 text-xs text-slate-500">版权交易管理 / 漫剧承制管理</p>
        <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">漫剧承制管理</h1>
            <p className="mt-2 text-sm text-slate-500">管理漫剧投稿、审核、签约和分集交付。投稿扣点与承制报价分别配置。</p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <form onSubmit={search} className="flex flex-wrap items-end gap-2">
              <label className="w-48 text-xs text-slate-600">漫剧名称
                <input className="mt-1 h-10 w-full rounded-lg border border-slate-200 px-3 text-sm" value={input} maxLength={120} placeholder="搜索漫剧名称" onChange={(event) => { setInput(event.target.value); if (!event.target.value) { setKeyword(''); setPage(1) } }} />
              </label>
              <FilterSelect label="商单状态" value={status} onChange={(value) => { setStatus(value); setPage(1) }} options={[["", "全部状态"], ...Object.entries(recruitmentLabels)]} />
              <button type="submit" className="h-10 rounded-lg bg-brand-600 px-3 text-sm text-white">查询</button>
              <button type="button" onClick={() => { setInput(''); setKeyword(''); setStatus(''); setPage(1) }} className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm">重置</button>
            </form>
            {canManage && <button type="button" onClick={() => setEditor({})} className="inline-flex h-10 items-center gap-2 rounded-lg bg-brand-600 px-4 text-sm font-medium text-white hover:bg-brand-700"><Plus size={17} aria-hidden="true" />新增商单</button>}
          </div>
        </div>
        {preview && <p className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" role="status">页面原型预览：当前显示示例商单。新增、编辑和审核仅在当前页面生效，刷新后会重置；尚未连接后台数据。</p>}
        {loadError && <p className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700" role="alert">{loadError}<button type="button" className="ml-3 underline" onClick={() => setRefreshVersion(current => current + 1)}>重试</button></p>}
        {notice && <p className="mb-4 rounded-xl bg-green-50 p-3 text-sm text-green-700" role="status">{notice}</p>}
        <div className="mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {[
            { label: '全部商单', count: preview ? commissions.length : stats.total, icon: FileText },
            { label: '招募中', count: preview ? commissions.filter((item) => item.status === 'recruiting').length : stats.recruiting, icon: Clock3 },
            { label: '试稿审核中', count: preview ? commissions.filter(item => item.applications.some(application => application.status === 'pending')).length : stats.reviewing, icon: UserRound },
            { label: '待签约', count: preview ? commissions.filter(item => item.status === 'awaiting_contract').length : stats.awaitingContract, icon: FileText },
            { label: '制作中', count: preview ? commissions.filter(item => item.status === 'producing').length : stats.producing, icon: Clapperboard },
          ].map(({ label, count, icon: Icon }) => (
            <div key={label} className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div><p className="text-sm text-slate-500">{label}</p><p className="mt-1 text-2xl font-semibold">{count}</p></div>
              <Icon className="text-slate-400" size={22} aria-hidden="true" />
            </div>
          ))}
        </div>
        <div className="min-h-64 flex-1 overflow-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full min-w-[860px] text-left text-sm">
            <thead className="sticky top-0 bg-slate-100 text-slate-600"><tr>
              {['封面', '漫剧名称', '承制报价', '试稿解锁扣点', '解锁人数', '试稿提交数', '当前状态', '操作'].map((label) => <th key={label} className="p-2">{label}</th>)}
            </tr></thead>
            <tbody>{visible.map((item) => {
              const waiting = preview ? item.applications.filter((application) => application.status === 'pending').length : item.pendingApplicationCount || 0
              return <tr key={item.id} className="border-t border-slate-100 align-top">
                <td className="p-2">{item.covers[0] ? <ProductionCommissionCover id={item.id} url={item.covers[0]} alt={`${item.title}封面`} className="h-16 w-12 rounded-md object-cover" /> : <div className="flex h-16 w-12 items-center justify-center rounded-md bg-slate-100 text-slate-300"><ImageIcon size={20} aria-hidden="true" /></div>}</td>
                <td className="min-w-28 p-2 font-medium">{item.title}</td>
                <td className="whitespace-nowrap p-2">{item.rateYuanPerMinute ?? commissionRateLabel.replace('元/分钟', '')}元/分钟</td>
                <td className="whitespace-nowrap p-2">{item.submissionPoints ?? 800} 点</td>
                <td className="whitespace-nowrap p-2 text-slate-600">{item.paidParticipantCount ?? 0} 人</td>
                <td className="p-2 text-slate-600">{preview ? item.applications.length : item.applicationCount || 0} 份{waiting > 0 && <span className="block text-xs text-amber-700">待审 {waiting}</span>}</td>
                <td className="p-2"><span className={'inline-block whitespace-nowrap rounded-full px-2 py-1 text-xs ' + statusStyle(item.status)}>{recruitmentLabels[item.status]}</span></td>
                <td className="p-2"><div className="flex items-center gap-1">
                  <button type="button" className="whitespace-nowrap rounded-lg border border-slate-300 px-2 py-1.5 text-xs hover:bg-slate-50" onClick={() => setDetailOffer(item)}>查看详情</button>
                  {canManage && !item.winnerApplicationId && ['draft', 'recruiting', 'paused', 'ended'].includes(item.status) && <button type="button" className="rounded-lg px-2 py-1.5 text-xs text-brand-700 hover:bg-brand-50" onClick={() => setEditor({ offer: item })}>编辑</button>}
                  {canManage && <button type="button" className="rounded-lg px-2 py-1.5 text-xs text-red-600 hover:bg-red-50 disabled:opacity-40" disabled={deleteBusyId === item.id} onClick={() => { setDeleteTarget(item); setDeleteConfirmTitle('') }}>{deleteBusyId === item.id ? '删除中…' : '删除'}</button>}
                </div></td>
              </tr>
            })}</tbody>
          </table>
          {!visible.length && <p className="p-10 text-center text-sm text-slate-500">{loading ? '正在加载商单…' : '没有符合条件的商单'}</p>}
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-slate-600">
          <span>共 {recordCount} 条记录</span>
          <div className="flex items-center gap-2">
            <button type="button" disabled={currentPage <= 1} onClick={() => setPage(currentPage - 1)} className="rounded-lg border border-slate-200 px-3 py-1.5 disabled:opacity-40">上一页</button>
            <span className="rounded-lg bg-brand-600 px-3 py-1.5 text-white">{currentPage}</span>
            <button type="button" disabled={currentPage >= pages} onClick={() => setPage(currentPage + 1)} className="rounded-lg border border-slate-200 px-3 py-1.5 disabled:opacity-40">下一页</button>
            <span>跳至</span><input aria-label="跳至页码" type="number" min={1} max={pages} value={jump} onChange={(event) => setJump(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); setPage(Math.max(1, Math.min(pages, Number(jump) || 1))) } }} className="w-16 rounded-lg border border-slate-200 px-2 py-1.5" /><span>页</span>
          </div>
        </div>
      </div>
      {editor && <ProductionCommissionEditor key={editor.offer?.id || 'new'} offer={editor.offer} onClose={() => setEditor(null)} onSave={save} />}
      {detailOffer && <ProductionCommissionDetail offer={detailOffer} onClose={() => { setDetailOffer(null); setRefreshVersion(current => current + 1) }} onChanged={(next) => { if (next) setCommissions(current => current.map(item => item.id === next.id ? { ...item, ...next, applications: next.applications ?? item.applications } : item)); else if (!preview) setRefreshVersion(current => current + 1) }} preview={preview} readOnly={!canManage} />}
      {deleteTarget && <AdminDialog title={`删除商单：${deleteTarget.title}`} onClose={() => setDeleteTarget(null)} busy={deleteBusyId === deleteTarget.id}>
        <div className="space-y-5 text-sm text-slate-700">
          <p>请选择处理方式。以下操作都会从管理列表和前台广场移除商单。</p>
          <div className="rounded-xl border border-slate-200 p-4">
            <h3 className="font-semibold text-slate-900">隐藏商单，保留记录</h3>
            <p className="mt-1 text-slate-600">保留投稿、扣点、合同及交付资料，便于后续审计。</p>
            <button type="button" disabled={Boolean(deleteBusyId)} onClick={() => void remove(deleteTarget, 'archive')} className="mt-3 rounded-lg border border-slate-300 px-3 py-2 font-medium disabled:opacity-40">隐藏并保留记录</button>
          </div>
          <div className="rounded-xl border border-red-200 bg-red-50 p-4">
            <h3 className="font-semibold text-red-800">永久删除商单记录</h3>
            <p className="mt-1 text-red-700">商单、投稿和交付记录会从数据库永久删除且无法恢复；上传文件将无法再通过商单访问。已发生的扣点流水与操作审计仍会保留。</p>
            {(deleteTarget.firstPaymentStatus === 'due' || deleteTarget.finalPaymentStatus === 'due') && <p className="mt-2 font-medium text-red-800">商单仍有待结算款项，结清前不能永久删除。</p>}
            <label className="mt-3 block text-red-800">输入完整漫剧名称以确认：<strong>{deleteTarget.title}</strong>
              <input className="mt-2 block w-full rounded-lg border border-red-300 bg-white px-3 py-2" value={deleteConfirmTitle} onChange={event => setDeleteConfirmTitle(event.target.value)} autoComplete="off" />
            </label>
            <button type="button" disabled={Boolean(deleteBusyId) || deleteConfirmTitle !== deleteTarget.title || deleteTarget.firstPaymentStatus === 'due' || deleteTarget.finalPaymentStatus === 'due'} onClick={() => void remove(deleteTarget, 'permanent')} className="mt-3 rounded-lg bg-red-700 px-3 py-2 font-medium text-white disabled:opacity-40">永久删除</button>
          </div>
        </div>
      </AdminDialog>}
    </Layout>
  )
}
